"use client";

import { useCallback, useEffect, useRef } from "react";

const DEFAULT_CHARS = ".:+=-~";
const DEFAULT_CELL = 20;
const DEFAULT_SPEED = 0.00015;
const DEFAULT_ALPHA = 0.18;
const DEFAULT_FONT_SCALE = 0.55;

type AsciiBackgroundProps = {
	chars?: string;
	cell?: number;
	speed?: number;
	alpha?: number;
	fontScale?: number;
};

export const AsciiBackground = ({
	chars = DEFAULT_CHARS,
	cell = DEFAULT_CELL,
	speed = DEFAULT_SPEED,
	alpha: maxAlpha = DEFAULT_ALPHA,
	fontScale = DEFAULT_FONT_SCALE,
}: AsciiBackgroundProps) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const frameRef = useRef<number>(0);
	const gridRef = useRef<string[][]>([]);
	const startRef = useRef<number>(0);

	const buildGrid = useCallback(
		(cols: number, rows: number) => {
			const grid: string[][] = [];
			for (let r = 0; r < rows; r++) {
				const row: string[] = [];
				for (let c = 0; c < cols; c++) {
					row.push(chars[Math.floor(Math.random() * chars.length)]);
				}
				grid.push(row);
			}
			return grid;
		},
		[chars],
	);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const resize = () => {
			const dpr = window.devicePixelRatio || 1;
			const rect = canvas.parentElement?.getBoundingClientRect();
			if (!rect) return;
			canvas.width = rect.width * dpr;
			canvas.height = rect.height * dpr;
			canvas.style.width = `${rect.width}px`;
			canvas.style.height = `${rect.height}px`;
			ctx.scale(dpr, dpr);

			const cols = Math.ceil(rect.width / cell) + 1;
			const rows = Math.ceil(rect.height / cell) + 1;
			gridRef.current = buildGrid(cols, rows);
		};

		resize();
		startRef.current = performance.now();

		const fg = getComputedStyle(document.documentElement)
			.getPropertyValue("--foreground")
			.trim();

		const draw = (now: number) => {
			const rect = canvas.parentElement?.getBoundingClientRect();
			if (!rect) return;
			const t = (now - startRef.current) * speed;

			ctx.clearRect(0, 0, rect.width, rect.height);
			ctx.font = `${cell * fontScale}px ui-monospace, SFMono-Regular, monospace`;
			ctx.textAlign = "center";
			ctx.textBaseline = "middle";

			const grid = gridRef.current;
			for (let r = 0; r < grid.length; r++) {
				const row = grid[r];
				for (let c = 0; c < row.length; c++) {
					const x = c * cell;
					const y = r * cell;

					const wave = Math.sin(c * 0.15 + r * 0.1 - t * 2) * 0.5 + 0.5;
					const alpha = wave * maxAlpha;

					if (alpha < 0.005) continue;

					if (Math.random() < 0.0002) {
						row[c] = chars[Math.floor(Math.random() * chars.length)];
					}

					ctx.fillStyle = fg.replace(")", ` / ${alpha})`);
					ctx.fillText(row[c], x, y);
				}
			}

			frameRef.current = requestAnimationFrame(draw);
		};

		frameRef.current = requestAnimationFrame(draw);

		window.addEventListener("resize", resize);
		return () => {
			cancelAnimationFrame(frameRef.current);
			window.removeEventListener("resize", resize);
		};
	}, [buildGrid, cell, speed, maxAlpha, fontScale, chars]);

	return (
		<canvas
			ref={canvasRef}
			className="pointer-events-none absolute inset-0 h-full w-full"
		/>
	);
};
